/**
 * An audit row's Detail sentence (TMMAPP-0103; TMMAPI-0143's `detail {key, params}`). The server
 * serves the key (`audit.<action>`) and the primitive params; tmm-app owns the sentence in the
 * catalog. Any param can be absent (a sharer's view, the owner-only `profileName`), and a missing
 * `{{param}}` must never reach the screen, so the order is: the sentence if it renders whole, else
 * its `_short` form, else nothing (the caller shows the Where text). An action with no catalog key
 * is nothing too. A served wire enum reads its `enum.<param>.<VALUE>` word in the reader's language
 * (TMMAPP-0113), with `humanizeToken` as the fallback for a value the catalog doesn't know.
 */
import type { TFunction } from 'i18next';
import type { AuditEvent } from '@/api/audit';
import { formatCivilDate, formatInstant } from '@/i18n/format';
import { formatScalar, type FormatCtx } from './formatAuditValue';
import { humanizeToken } from './labels';

const MISSING = '\u0000missing';
const DETAIL_PREFIX = 'audit.';
const ENUM_KEYS: ReadonlySet<string> = new Set([
  'kind',
  'type',
  'status',
  'direction',
  'mode',
  'origin',
  'view',
  'action',
  'reason',
  'voidReason',
  // A share's role (TMMAPI-0146 `SHARE_ROLE_CHANGED`): VIEWER / MANAGER.
  'role',
  // TMMAPP-0113: `SUBSCRIPTION_INTERVAL_CHANGED`'s MONTH / YEAR.
  'interval',
  // TMMAPP-0132: a balance check's IMPORT / MANUAL / PROVIDER.
  'source',
]);
/**
 * Counts that pick a plural form (TMMAPP-0113, the 0110 review): the served param reaches i18next as
 * `count`, a number, so the sentence's `_one` / `_many` / `_other` forms apply.
 */
const PLURAL_PARAMS: Readonly<Record<string, string>> = {
  contributions_removed: 'count',
  virtual_account_removed_by_owner_erasure: 'count',
  export_produced: 'rowCount',
  payee_order_changed: 'count',
  account_opening_moved: 'earlierRows',
};
/**
 * Params that are amounts in minor units without the `…Minor` suffix, per action (TMMAPI-0147:
 * `ENVELOPE_BUDGET_CHANGED` serves the budget before and after as `from` / `to`).
 */
const MONEY_PARAMS: Readonly<Record<string, ReadonlySet<string>>> = {
  envelope_budget_changed: new Set(['from', 'to']),
};
/**
 * An action whose sentence turns on a served boolean: TMMAPI-0148's `PLAN_LINE_SETTLED` is a mark
 * received (`settled: true`) or its undo (`settled: false`), each with its own sentence and short form.
 */
const FALSE_SENTENCES: Readonly<Record<string, { param: string; keys: readonly string[] }>> = {
  plan_line_settled: {
    param: 'settled',
    keys: ['audit.plan_line_settled_undone', 'audit.plan_line_settled_undone_short'],
  },
  // TMMAPP-0113: a switch turned off reads its own sentence.
  payee_ai_exclusion_set: {
    param: 'aiExcluded',
    keys: ['audit.payee_ai_exclusion_set_undone', 'audit.payee_ai_exclusion_set_undone_short'],
  },
  automation_changed: { param: 'enabled', keys: ['audit.automation_changed_undone'] },
  ai_wallet_auto_recharge_changed: {
    param: 'autoRecharge',
    keys: ['audit.ai_wallet_auto_recharge_changed_undone'],
  },
};
const CIVIL = /^\d{4}-\d{2}-\d{2}$/;
const INSTANT = /^\d{4}-\d{2}-\d{2}T/;
const ENUM_TOKEN = /^[A-Z][A-Z0-9]*(?:_[A-Z0-9]+)+$|^[A-Z]{2,}$/;

/** A served param as the sentence prints it: money, civil dates and instants through Intl. */
function printParam(
  t: TFunction,
  key: string,
  value: string | number | boolean,
  ctx: FormatCtx,
  currency: string | null,
): string {
  if (typeof value === 'string') {
    if (CIVIL.test(value)) {
      try {
        return formatCivilDate(value, ctx.locale);
      } catch {
        return value;
      }
    }
    if (INSTANT.test(value)) {
      try {
        return formatInstant(value, ctx.locale, ctx.timeZone);
      } catch {
        return value;
      }
    }
    // Only the wire's own enums are worded; a name a person typed ("HSA_FUND") reads as typed.
    if (!ENUM_KEYS.has(key) || !ENUM_TOKEN.test(value)) return value;
    return t(`enum.${key}.${value}`, { defaultValue: humanizeToken(value) });
  }
  return formatScalar(key, value, ctx, currency);
}

/** The sentence for a row, or `null` when the Where text should stand in. */
export function auditDetailText(t: TFunction, event: AuditEvent, ctx: FormatCtx): string | null {
  const detail = event.detail;
  if (detail === null) return null;
  // The key is the action in lower case under `audit.`; a key outside that family has no sentence here.
  if (!detail.key.startsWith(DETAIL_PREFIX)) return null;
  const action = detail.key.slice(DETAIL_PREFIX.length);
  const currency = event.currency ?? ctx.currency;
  const params: Record<string, string> = {};
  for (const [key, value] of Object.entries(detail.params)) {
    if (typeof value === 'string' && value.trim().length === 0) continue;
    const money = typeof value === 'number' && MONEY_PARAMS[action]?.has(key) === true;
    params[key] = printParam(t, money ? `${key}Minor` : key, value, ctx, currency);
  }
  const plural = PLURAL_PARAMS[action];
  const served = plural !== undefined ? detail.params[plural] : undefined;
  // Audit params are untyped on the wire: a count may arrive as a number or a numeric string.
  const servedCount =
    typeof served === 'number'
      ? served
      : typeof served === 'string' && /^\d+$/.test(served)
        ? Number(served)
        : NaN;
  const count = Number.isFinite(servedCount) ? { count: servedCount } : {};
  const variant = FALSE_SENTENCES[action];
  const keys =
    variant !== undefined && detail.params[variant.param] === false
      ? variant.keys
      : [`audit.${action}`, `audit.${action}_short`];
  for (const key of keys) {
    const text = t(key, { ...params, ...count, defaultValue: MISSING });
    if (text !== MISSING && text.trim().length > 0 && !text.includes('{{')) return text;
  }
  return null;
}
